import { useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type Resolver } from "react-hook-form";
import {
  FormattedMessage,
  injectIntl,
  type intlFormatPropTypes,
} from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { Button } from "@ps-design-system/components/Button";
import { Divider } from "@ps-design-system/components/Divider";
import { Form } from "@ps-design-system/components/Form";
import { Text } from "@ps-design-system/components/Text";

import {
  AddressFields,
  buildCountryOptions,
  buildZoneOptions,
  countryTooltipTriggerMessage,
  FieldLabelWithTooltip,
  findCountryByCode,
} from "../../AddressForm";
import { selectEnvCountries } from "../../app-main/selectors/env.selectors";
import { selectBillingFeeProfileId } from "../../app-main/selectors/fee-profile.selectors";
import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import {
  selectAccountCountry,
  selectSpaceAddress,
} from "../../app-main/selectors/space.selectors";

import {
  ADDRESS_FORM_ID,
  SPACE_ADDRESS_FIELDS,
} from "./AddSpaceAddressForm.consts";
import {
  createAddressFormSchema,
  type AddressFormValues,
} from "./AddSpaceAddressForm.schema";
import { submitAddressForm } from "./AddSpaceAddressForm.submit";
import type { Country, Dispatch } from "./AddSpaceAddressForm.types";
import { buildDefaultValues } from "./AddSpaceAddressForm.utils";
import { BillingAddressSection } from "./BillingAddressSection";

type AddSpaceAddressFormProps = {
  accessToken: string;
  /** Countries in the host's locale — drives every label the host reads. */
  allCountries: Country[];
  /** The same countries in English — drives validation, see `resolver` below. */
  globalCountries: Country[];
  hasBillingAddress: boolean;
  intl: intlFormatPropTypes;
  isEdit: boolean;
  spaceId?: string;
};

const AddSpaceAddressFormComponent = ({
  accessToken,
  allCountries,
  globalCountries,
  hasBillingAddress,
  intl,
  isEdit,
  spaceId,
}: AddSpaceAddressFormProps) => {
  const dispatch = useDispatch() as Dispatch;
  const accountCountry = useSelector(selectAccountCountry);
  const envCountries = useSelector(selectEnvCountries);
  const feeProfileId = useSelector(selectBillingFeeProfileId);
  const spaceAddress = useSelector(selectSpaceAddress);
  const ssoId = useSelector(selectProfileSSOid);

  // Validation reads the *English* country: the error-message lookups are keyed by
  // Shopify's English field labels ("ZIP code", "Prefecture"), so feeding them the
  // localised country would miss every key. Building the schema inside the resolver
  // also means it can never go stale against the country currently selected.
  const resolver: Resolver<AddressFormValues> = (values, context, options) =>
    zodResolver(
      createAddressFormSchema({
        intl,
        selectedCountry: findCountryByCode(globalCountries, values.country),
      }),
    )(values, context, options);

  const form = useForm<AddressFormValues>({
    resolver,
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: buildDefaultValues({
      country: accountCountry ?? spaceAddress.country ?? "",
      spaceAddress,
    }),
  });

  const country = form.watch("country");
  const billingChoice = form.watch("billingChoice");

  const selectedCountry = useMemo(
    () => findCountryByCode(allCountries, country),
    [allCountries, country],
  );
  const countryOptions = useMemo(
    () => buildCountryOptions({ allCountries, envCountries }),
    [allCountries, envCountries],
  );
  const zoneOptions = useMemo(
    () => buildZoneOptions(selectedCountry),
    [selectedCountry],
  );

  const hasListedSpace = Boolean(accountCountry);
  const showsBillingSection = !hasListedSpace || !hasBillingAddress;

  const handleSubmit = async (values: AddressFormValues) => {
    form.clearErrors("root.serverError");

    const result = await submitAddressForm({
      accessToken,
      dispatch,
      feeProfileId,
      hasBillingAddress,
      hasListedSpace,
      intl,
      isEdit,
      spaceId,
      ssoId: ssoId ?? "",
      values,
    });

    if (result.status === "error") {
      form.setError("root.serverError", { message: result.message });
    }
  };

  return (
    <div className="AddressForm">
      <div className="ps-row-nested">
        <div className="col-xs-12">
          <Text element="h2" typeface="headline-4">
            <FormattedMessage
              id="hosting.address_form.space_address_heading"
              defaultMessage="Space Address"
            />
          </Text>
          <Text element="p" typeface="body-2">
            <FormattedMessage
              id="hosting.address_form.space_address_text"
              defaultMessage="Your space’s address will not be displayed publicly on your Peerspace listing. It will only be shared with a guest after they book the space."
            />
          </Text>
          <Divider />
        </div>
      </div>

      <div className="ps-row-nested">
        <div className="col-sm-8">
          <Form
            {...form}
            fullWidth={true}
            id={ADDRESS_FORM_ID}
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <AddressFields
              countryDisabled={hasListedSpace}
              countryLabel={
                hasListedSpace ? (
                  <FieldLabelWithTooltip
                    body={
                      <FormattedMessage
                        id="hosting.address_form.space_tooltip"
                        defaultMessage="To add a space in a different country, please create another account."
                      />
                    }
                    label={
                      <FormattedMessage
                        id="hosting.address_form.country_label"
                        defaultMessage="Country"
                      />
                    }
                    triggerLabel={intl.formatMessage(
                      countryTooltipTriggerMessage,
                    )}
                  />
                ) : undefined
              }
              countryLabels={selectedCountry?.labels}
              countryOptions={countryOptions}
              names={SPACE_ADDRESS_FIELDS}
              testIdPrefix="space"
              zoneOptions={zoneOptions}
            />

            {showsBillingSection && (
              <BillingAddressSection
                billingChoice={billingChoice}
                countryDisabled={hasListedSpace}
                countryLabels={selectedCountry?.labels}
                countryOptions={countryOptions}
                zoneOptions={zoneOptions}
              />
            )}
          </Form>
        </div>
      </div>

      <div className="ps-row-nested">
        <div className="col-xs-12">
          <Divider />
          <div className="flex space-between">
            {!isEdit && (
              <Button
                appearance="black"
                href={spaceId ? `/your-space/${spaceId}` : "/your-space"}
                variant="outline-1"
              >
                <FormattedMessage
                  id="hosting.address_form.back_link_text"
                  defaultMessage="Back"
                />
              </Button>
            )}
            <Button
              appearance="purple"
              disabled={form.formState.isSubmitting}
              form={ADDRESS_FORM_ID}
              showSpinner={form.formState.isSubmitting}
              testId="address"
              type="submit"
            >
              {isEdit ? (
                <FormattedMessage
                  id="hosting.address_form.button_text_done"
                  defaultMessage="Done"
                />
              ) : (
                <FormattedMessage
                  id="hosting.address_form.button_text_next"
                  defaultMessage="Next"
                />
              )}
            </Button>
          </div>
          {form.formState.errors.root?.serverError && (
            <Text className="tw-text-red-800" element="p" typeface="body-1">
              {form.formState.errors.root.serverError.message}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

const AddSpaceAddressForm = injectIntl(AddSpaceAddressFormComponent);

export { AddSpaceAddressForm };

export type { AddSpaceAddressFormProps };
